import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import AvailableOn from "../AvailableOn/AvailableOn";
import { syncCartItems } from "../../services/productService";
import { clearStoredUserSession, saveAuthenticatedProfile } from "../../utils/authStorage";
import "./Register.css";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const extractApiMessage = (error, fallback) => {
  const data = error?.response?.data;

  if (typeof data?.message === "string") {
    return data.message;
  }

  if (typeof data?.errors === "string") {
    return data.errors;
  }

  if (data?.errors && typeof data.errors === "object") {
    const firstError = Object.values(data.errors).flat().find(Boolean);
    if (firstError) {
      return firstError;
    }
  }

  return fallback;
};

const getUserIdFromResponse = (data) => {
  const user = data?.data;

  if (!user || typeof user === "string") {
    return null;
  }

  return (
    user.user_id ||
    user.id ||
    user.customer_id ||
    user.user?.user_id ||
    user.user?.id ||
    null
  );
};

const fetchAuthenticatedUser = async (token) => {
  const { data } = await axios.get(`${API_BASE_URL}/auth/get-user-details`, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      token,
    },
  });

  return data?.data || {};
};

const getStoredCartItems = () => {
  try {
    return Object.values(JSON.parse(localStorage.getItem("carts") || "{}"));
  } catch (error) {
    return [];
  }
};

function Register() {
  const navigate = useNavigate();
  const [register, setRegister] = useState(true);
  const [otp, setOtp] = useState(false);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  const trimmedName = name.trim();
  const normalizedMobile = mobile.replace(/\D/g, "");

  useEffect(() => {
    if (localStorage.getItem("token")) {
      navigate("/profile", { replace: true });
    }
  }, [navigate]);

  const handleOTP = () => {
    setRegister(false);
    setOtp(true);
  };

  const backRegister = () => {
    setOtp(false);
    setRegister(true);
    setVerificationCode("");
  };

  const buildGenerateOtpFormData = () => {
    const formData = new FormData();
    formData.append("name", trimmedName);
    formData.append("mobile", normalizedMobile);
    return formData;
  };

  const buildVerifyOtpFormData = () => {
    const formData = new FormData();
    formData.append("mobile", normalizedMobile);
    formData.append("otp", verificationCode);
    return formData;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!trimmedName) {
      toast.info("Please enter your name");
      return;
    }

    if (!normalizedMobile) {
      toast.info("Please enter mobile number");
      return;
    }

    if (normalizedMobile.length !== 10) {
      toast.info("Please enter a valid 10 digit mobile number");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/generate-otp`,
        buildGenerateOtpFormData(),
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (data?.status === false) {
        toast.error(data?.message || "Failed sending OTP");
        return;
      }

      localStorage.setItem("auth_mobile", normalizedMobile);
      localStorage.setItem("auth_name", trimmedName);
      toast.success(data?.message || "OTP sent successfully");
      handleOTP();
    } catch (error) {
      toast.error(extractApiMessage(error, "Failed sending OTP"));
      console.log("Failed Sending OTP", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!verificationCode) {
      toast.info("Please enter OTP");
      return;
    }

    if (verificationCode.length !== 4) {
      toast.info("Please enter a valid 4 digit OTP");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/verify-otp`,
        buildVerifyOtpFormData(),
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (data?.status === false) {
        toast.error(data?.message || "OTP verification failed");
        return;
      }

      const token = data?.token;
      const verifiedMobile =
        typeof data?.data === "string" ? data.data : data?.data?.mobile;
      const userId = getUserIdFromResponse(data);

      if (!token) {
        toast.error("OTP verified, but login token was missing");
        return;
      }

      clearStoredUserSession({ preserveCart: true });
      localStorage.setItem("token", token);
      localStorage.setItem("auth_mobile", verifiedMobile || normalizedMobile);

      let resolvedUserId = userId;
      try {
        const authenticatedUser = await fetchAuthenticatedUser(token);
        resolvedUserId =
          authenticatedUser?.id ||
          authenticatedUser?.user_id ||
          authenticatedUser?.customer_id ||
          userId;

        saveAuthenticatedProfile(authenticatedUser, {
          name: trimmedName,
          mobile: verifiedMobile || normalizedMobile,
          userId,
        });
      } catch (error) {
        console.error("Failed fetching authenticated user details", error);
      }

      if (resolvedUserId) {
        localStorage.setItem("user_id", String(resolvedUserId));
      }

      const storedCartItems = getStoredCartItems();

      if (resolvedUserId && storedCartItems.length > 0) {
        try {
          await syncCartItems({ user_id: resolvedUserId, items: storedCartItems });
        } catch (error) {
          console.error("Failed syncing guest cart after login", error);
          toast.error("Login successful, but cart sync failed");
        }
      }

      window.dispatchEvent(new CustomEvent("login-modal-authenticated"));
      window.dispatchEvent(new CustomEvent("auth-state-changed"));
      toast.success(data?.message || "OTP verified successfully");
      navigate("/profile");
    } catch (error) {
      toast.error(extractApiMessage(error, "OTP verification failed"));
      console.log("Registration Failed ", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Kuku Namkeen | Sign In</title>
        <meta
          name="description"
          content="Sign in to Kuku Namkeen using your mobile number."
        />
      </Helmet>

      <ToastContainer />
      <main className="login-page">
        <section className="section-header-title">
          <div className="max-w-6xl mx-auto px-4">
            <h1>SIGN IN</h1>
          </div>
        </section>
        <img
          src="/Image/border-bottom-title.png"
          className="w-full header-title-image"
          alt=""
        />

        <section className="login-section">
          <div className="login-container">
            <div
              className="login-section-inner"
              style={{
                backgroundImage:
                  'url("/kuku-assets/images/login backside.png")',
              }}
            >
              <div className="login-layout">
                <div className="login-visual-space" aria-hidden="true"></div>

                <div className="login-card">
                  {register && (
                    <>
                      <div className="login-card-heading">
                        <h2>Let's Get Started!</h2>
                        <p>Register using your mobile number &amp; explore more.</p>
                      </div>

                      <form onSubmit={handleRegister}>
                        <div className="login-floating-field">
                          <input
                            id="login-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder=" "
                            autoComplete="name"
                          />
                          <label htmlFor="login-name">
                            Your Name <sup>*</sup>
                          </label>
                        </div>

                        <div className="login-floating-field">
                          <input
                            id="login-mobile"
                            type="tel"
                            inputMode="numeric"
                            maxLength={10}
                            value={mobile}
                            onChange={(e) =>
                              setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                            }
                            placeholder=" "
                            autoComplete="tel"
                          />
                          <label htmlFor="login-mobile">
                            Your Mobile <sup>*</sup>
                          </label>
                        </div>

                        <button
                          className="login-submit-btn"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Sending..." : "Get OTP"}
                        </button>
                      </form>

                      {/* <p className="login-or-text">or Sign in with</p>
                      <div className="login-google-wrap">
                        <img
                          src="/kuku-assets/images/google-button.svg"
                          className="login-google-image"
                          alt="Sign in with Google"
                        />
                      </div> */}
                    </>
                  )}

                  {otp && (
                    <>
                      <div className="login-card-heading">
                        <h2>OTP Verification</h2>
                        <p>We have sent a code to {normalizedMobile}</p>
                      </div>

                      <form onSubmit={handleSubmit}>
                        <div className="login-floating-field">
                          <input
                            id="login-otp"
                            type="tel"
                            inputMode="numeric"
                            maxLength={4}
                            value={verificationCode}
                            onChange={(e) =>
                              setVerificationCode(
                                e.target.value.replace(/\D/g, "").slice(0, 4)
                              )
                            }
                            placeholder=" "
                            autoComplete="one-time-code"
                          />
                          <label htmlFor="login-otp">
                            Enter OTP <sup>*</sup>
                          </label>
                        </div>

                        <button
                          className="login-submit-btn"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Verifying..." : "Submit OTP"}
                        </button>
                      </form>

                      <div className="login-otp-actions">
                        <button type="button" onClick={backRegister}>
                          Change mobile
                        </button>
                        <button
                          type="button"
                          onClick={handleRegister}
                          disabled={loading}
                        >
                          Resend OTP
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <AvailableOn />
      </main>
    </>
  );
}

export default Register;
